import React from 'react'

import { useConnect } from 'redux-bundler-hook'

import { Typography } from '@mui/material'

import BaseFormatter from '~/src/Annotations/Formatters/Base'
import { dtLabelSanitizer, formattedNumber } from '~/src/Lib/Utils'

import InterruptionFormatter, { interruptionFormatterUtil } from './InterruptionFormatter'

export { default as IconFormatter } from './IconFormatter'

const showLocationInBody = props => {
  const { annotation: { data }, fields, room, zone } = props
  return !!data?.zone && !(room || zone) && !fields?.includes?.('location')
}

const formatterUtils = {
  default: props => {
    const { annotation } = props
    const { message, subject } = annotation

    return {
      title: subject,
      body: message,
    }
  },
  defaultWithDataType: props => {
    const { annotation } = props
    const { data, subject } = annotation

    return {
      title: dtLabelSanitizer(subject),
      body: `reading: ${data?.value} ${'\u2014'} threshold: ${data?.threshold}`,
      location: showLocationInBody(props) ? `${data?.zone}: ` : '',
    }
  },
  BATTERY_LOW: props => {
    const { annotation, entities } = props
    const { devices } = entities
    const { data, device: deviceId, message } = annotation
    const { [deviceId]: device } = devices

    const serialNumber = data?.serialNumber ?? device?.serialNumber
    const deviceName = data?.deviceName ?? device?.deviceName

    return {
      title: `
      ${deviceName ? `${deviceName} ` : ''}
      ${deviceName && serialNumber && deviceName !== serialNumber ? `(${serialNumber}) ` : ''}
      ${(!deviceName && serialNumber) ? `${serialNumber} ` : ''}
      low battery
      `,
      body: data?.battery ? `Battery level: ${formattedNumber(data.battery, 1)}%` : message,
    }
  },
  LIGHT_SCHEDULE: props => {
    const { annotation } = props
    const { data } = annotation
    const { actualOnOrOff, expectedOnOrOff, time } = data

    return {
      title: `Lights still ${actualOnOrOff}`,
      body: `Lights were scheduled to turn ${expectedOnOrOff} at ${time}`,
    }
  },
  DEVICE_UNPLUGGED: interruptionFormatterUtil,
  DEVICE_DISCONNECTED: interruptionFormatterUtil,
  GATEWAY_DISCONNECTED: interruptionFormatterUtil,
  PUCK_DISCONNECTED: interruptionFormatterUtil,
}

export const formatNotification = dataToFormat => {
  const { annotation } = dataToFormat

  if (!annotation) {
    return null
  }

  const { dataTypeKey, key } = annotation

  let { [key]: formatterUtil } = formatterUtils

  if (!formatterUtil) {
    formatterUtil = dataTypeKey
      ? formatterUtils.defaultWithDataType
      : formatterUtils.default
  }

  return formatterUtil(dataToFormat)
}

export const formatters = {
  default: props => {
    const { annotation, color, ...passthruProps } = props
    const { message, subject } = annotation

    return (
      <BaseFormatter
        wrap
        color={color}
        {...passthruProps}
        title={<Typography variant="body1" color={color}>{subject}</Typography>}
        body={<Typography variant="body2">{message}</Typography>}
      />
    )
  },
  defaultWithDataType: React.memo(props => {
    const { annotation, color, ...passthruProps } = props
    const { data, subject } = annotation

    return (
      <BaseFormatter
        wrap
        color={color}
        {...passthruProps}
        title={(
          <Typography
            variant="body1"
            color="textPrimary"
            dangerouslySetInnerHTML={{ __html: dtLabelSanitizer(subject) }}
          />
        )}
        body={(
          <Typography
            variant="body2"
            color={color}
          >
            {showLocationInBody(props) ? `${data?.zone}: ` : ''}
            reading: {data?.value} {'\u2014'} threshold: {data?.threshold}
          </Typography>
        )}
      />
    )
  }),
  BATTERY_LOW: React.memo(props => {
    const { annotation, classes, color, ...passthruProps } = props
    const { data, device: deviceId, message } = annotation
    const { devices } = useConnect('selectDevices')
    const { [deviceId]: device } = devices

    const serialNumber = data?.serialNumber ?? device?.serialNumber
    const deviceName = data?.deviceName ?? device?.deviceName

    return (
      <BaseFormatter
        wrap
        classes={classes}
        color={color}
        {...passthruProps}
        title={(
          <Typography
            variant="body1"
            color="textPrimary"
          >
            {deviceName ? `${deviceName} ` : null}
            {deviceName && serialNumber && deviceName !== serialNumber ? `(${serialNumber}) ` : null}
            {(!deviceName && serialNumber) ? `${serialNumber} ` : null}
            low battery
          </Typography>
        )}
        body={data?.battery ? (
          <Typography
            variant="body2"
            color={color}
          >
            Battery level: {formattedNumber(data.battery, 1)}%
          </Typography>
        ) : message}
      />
    )
  }),
  LIGHT_SCHEDULE: React.memo(props => {
    const { annotation, color, ...passthruProps } = props
    const { data } = annotation
    const { actualOnOrOff, expectedOnOrOff, time } = data

    return (
      <BaseFormatter
        wrap
        color={color}
        {...passthruProps}
        title={(
          <Typography
            variant="body1"
            color="textPrimary"
          >
            {`Lights still ${actualOnOrOff}`}
          </Typography>
        )}
        body={(
          <Typography
            variant="body2"
            color={color}
          >
            {`Lights were scheduled to turn ${expectedOnOrOff} at ${time}`}
          </Typography>
        )}
      />
    )
  }),
  DEVICE_UNPLUGGED: InterruptionFormatter,
  DEVICE_DISCONNECTED: InterruptionFormatter,
  GATEWAY_DISCONNECTED: InterruptionFormatter,
  PUCK_DISCONNECTED: InterruptionFormatter,
}
